import { isNil } from "lodash";

export function areEqualArrays<T>(
    filter: ReadonlyArray<T> | undefined | null,
    comparedFilter: ReadonlyArray<T> | undefined | null,
): boolean {
    if (isNil(filter) || isNil(comparedFilter) || filter.length !== comparedFilter.length) {
        return false;
    }

    const comparedFilterSet = new Set(comparedFilter);

    return !filter.some((element) => !comparedFilterSet.has(element));
}

export function splitArray<T>(
    array: readonly T[],
    conditionFn: (param: T) => boolean,
    maxMatchedCount?: number,
): [readonly T[], readonly T[]] {
    const matched: T[] = [];
    const unmatched: T[] = [];

    const matchedArrayLimit = maxMatchedCount ?? array.length;

    for (const element of array) {
        const isMatchedArrayLimitReached = matched.length >= matchedArrayLimit;

        if (!isMatchedArrayLimitReached && conditionFn(element)) {
            matched.push(element);
        } else {
            unmatched.push(element);
        }
    }

    return [matched, unmatched];
}

/* @throws Error */
export function splitArrayByIndex<T>(array: readonly T[], elementIndex: number): [T[], T[]] {
    if (elementIndex < 0 || elementIndex >= array.length) {
        throw new Error("Invalid elementIndex provided.");
    }

    const firstPart = array.slice(0, elementIndex);
    const secondPart = array.slice(elementIndex);

    return [firstPart, secondPart];
}

/**
 * Checks whether two given arrays have the same elements
 * @param arrayA
 * @param arrayB
 */
export function haveSameElements<T>(arrayA: readonly T[], arrayB: readonly T[]) {
    if (arrayA.length !== arrayB.length) {
        return false;
    }

    const frequencyMapA = arrayA.reduce((map, value) => {
        map.set(value, (map.get(value) || 0) + 1);
        return map;
    }, new Map<T, number>());
    const frequencyMapB = arrayB.reduce((map, value) => {
        map.set(value, (map.get(value) || 0) + 1);
        return map;
    }, new Map<T, number>());

    return Array.from(frequencyMapA.entries()).every(([key, value]) => {
        return frequencyMapB.get(key) === value;
    });
}

export function elementsAreInTheSameOrder(
    initialSelectedColumnNames: readonly string[],
    selectedNames: readonly string[],
) {
    if (initialSelectedColumnNames.length !== selectedNames.length) {
        return false;
    }
    return initialSelectedColumnNames.every((name, index) => name === selectedNames[index]);
}

export function genericElementsAreInTheSameOrder<T>(
    arrayA: readonly T[],
    arrayB: readonly T[],
    compareFn: (a: T, b: T) => boolean,
) {
    if (arrayA.length !== arrayB.length) {
        return false;
    }
    return arrayA.every((el, index) => compareFn(el, arrayB[index]));
}

/**
 * Returns an array containing unique elements from given `list`
 * @param array
 */
export function keepOnlyUniqueElements<T>(array: readonly T[]) {
    if (array.length <= 1) {
        return array;
    }

    return Array.from(new Set(array));
}
